import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, ArrowLeft, MapPin } from 'lucide-react';
import { useUtmTaxonomyStore, type Zone } from '@/store/utm-taxonomy-store';
import UtmGeometryUpload from '@/components/utm/shared/UtmGeometryUpload';
import UtmGeometryPreview from '@/components/utm/shared/UtmGeometryPreview';
import UtmStyleEditor from '@/components/utm/shared/UtmStyleEditor';

type ZoneStyle = {
  polygon?: {
    fillColor?: [number, number, number, number];
    outlineColor?: [number, number, number, number];
    outlineWidth?: number;
  };
  line?: {
    color?: [number, number, number, number];
    width?: number;
  };
  point?: {
    fillColor?: [number, number, number, number];
    radius?: number;
  };
  label?: Record<string, unknown>;
};

const DEFAULT_STYLE: ZoneStyle = {
  polygon: {
    fillColor: [255, 0, 0, 100],
    outlineColor: [0, 0, 255, 200],
  },
  point: {
    radius: 5,
  },
};

const mergeStyles = (...styles: Array<ZoneStyle | undefined>): ZoneStyle =>
  styles.reduce<ZoneStyle>(
    (acc, style) => ({
      ...acc,
      ...(style || {}),
      polygon: { ...(acc.polygon || {}), ...(style?.polygon || {}) },
      line: { ...(acc.line || {}), ...(style?.line || {}) },
      point: { ...(acc.point || {}), ...(style?.point || {}) },
      label: { ...(acc.label || {}), ...(style?.label || {}) },
    }),
    {}
  );

const UtmZoneForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    groups,
    categories,
    fetchTaxonomy,
    fetchZones,
    zones,
    createZone,
    updateZone,
    uploadGeometry,
    updateGeometryBounds,
  } = useUtmTaxonomyStore();

  const [formData, setFormData] = useState({
    groupId: '',
    zoneCode: '',
    zoneName: '',
    status: 'NOT_PUBLISHED' as 'NOT_PUBLISHED' | 'PUBLISHED',
    activeStart: '',
    activeEnd: '',
    description: '',
    lowerBoundM: 0,
    lowerRef: 'AGL' as 'AGL' | 'AMSL',
    upperBoundM: 120,
    upperRef: 'AGL' as 'AGL' | 'AMSL',
  });

  const [geometry, setGeometry] = useState<any>(null);
  const [originalGeometry, setOriginalGeometry] = useState<any>(null);
  const [style, setStyle] = useState<ZoneStyle>(DEFAULT_STYLE);
  const [hasExistingZoneStyle, setHasExistingZoneStyle] = useState(false);
  const [styleTouched, setStyleTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [zoneCodeError, setZoneCodeError] = useState<string | undefined>();

  useEffect(() => {
    fetchTaxonomy();
    fetchZones();
  }, [fetchTaxonomy, fetchZones]);

  useEffect(() => {
    if (id) {
      const zone = zones.find((z) => z.id === id);
      if (zone) {
        const existingGeom = zone.geometry?.geom || null;
        const group = groups.find((g) => g.id === zone.groupId);
        const category = group ? categories.find((c) => c.id === group.categoryId) : undefined;
        const inheritedStyle = mergeStyles(category?.style as ZoneStyle, group?.style as ZoneStyle);
        const zoneStyle = zone.style as ZoneStyle | undefined;

        setFormData({
          groupId: zone.groupId,
          zoneCode: zone.zoneCode,
          zoneName: zone.zoneName,
          status: zone.status,
          activeStart: zone.activeStart || '',
          activeEnd: zone.activeEnd || '',
          description: zone.description || '',
          lowerBoundM: zone.geometry?.lowerBoundM ?? 0,
          lowerRef: zone.geometry?.lowerRef ?? 'AGL',
          upperBoundM: zone.geometry?.upperBoundM ?? 120,
          upperRef: zone.geometry?.upperRef ?? 'AGL',
        });
        setGeometry(existingGeom);
        setOriginalGeometry(existingGeom);
        setStyle(mergeStyles(DEFAULT_STYLE, inheritedStyle, zoneStyle));
        setHasExistingZoneStyle(Boolean(zoneStyle));
        setStyleTouched(false);
      }
    }
  }, [id, zones, groups, categories]);

  useEffect(() => {
    if (id || !formData.groupId) return;

    const selectedGroup = groups.find((group) => group.id === formData.groupId);
    const category = selectedGroup
      ? categories.find((c) => c.id === selectedGroup.categoryId)
      : undefined;
    setStyle(
      mergeStyles(DEFAULT_STYLE, category?.style as ZoneStyle, selectedGroup?.style as ZoneStyle)
    );
    setStyleTouched(false);
  }, [id, formData.groupId, groups, categories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(undefined);
    setZoneCodeError(undefined);

    if (!id && formData.groupId && formData.zoneCode) {
      const existingZone = zones.find(
        (z) => z.groupId === formData.groupId && z.zoneCode === formData.zoneCode
      );
      if (existingZone) {
        setError(
          'A zone with this code already exists in the selected group. Please use a different zone code.'
        );
        setZoneCodeError('This zone code is already in use for this group');
        setLoading(false);
        return;
      }
    }

    try {
      if (id) {
        const zoneUpdates: Partial<Zone> = {
          zoneName: formData.zoneName,
          status: formData.status,
          description: formData.description,
          activeStart: formData.activeStart || undefined,
          activeEnd: formData.activeEnd || undefined,
        };

        if (styleTouched || hasExistingZoneStyle) {
          zoneUpdates.style = style;
        }

        await updateZone(id, zoneUpdates);

        if (geometry) {
          await uploadGeometry(id, geometry);
          await updateGeometryBounds(id, {
            lowerBoundM: formData.lowerBoundM,
            lowerRef: formData.lowerRef,
            upperBoundM: formData.upperBoundM,
            upperRef: formData.upperRef,
          });
        }
      } else {
        const zonePayload: Partial<Zone> = {
          groupId: formData.groupId,
          zoneCode: formData.zoneCode,
          zoneName: formData.zoneName,
          status: formData.status,
          description: formData.description,
          activeStart: formData.activeStart || undefined,
          activeEnd: formData.activeEnd || undefined,
        };

        if (styleTouched) {
          zonePayload.style = style;
        }

        const createdZone = await createZone(zonePayload);

        if (geometry && createdZone.id) {
          await uploadGeometry(createdZone.id, geometry);
          await updateGeometryBounds(createdZone.id, {
            lowerBoundM: formData.lowerBoundM,
            lowerRef: formData.lowerRef,
            upperBoundM: formData.upperBoundM,
            upperRef: formData.upperRef,
          });
        }
      }

      navigate('/admin/uzamaps/zones');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save zone');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex items-start space-x-3 sm:items-center sm:space-x-4">
        <button
          onClick={() => navigate('/admin/uzamaps/zones')}
          className="p-2 text-muted-foreground hover:text-accent-foreground hover:bg-accent rounded transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
            {id ? 'Edit Zone' : 'Create Zone'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {id ? 'Update UTM zone details and properties' : 'Add a new UTM airspace zone'}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-card border border-border rounded-lg p-4 space-y-4 sm:p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Basic Information</h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Group *</label>
              <select
                value={formData.groupId}
                onChange={(e) => setFormData({ ...formData, groupId: e.target.value })}
                className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
                required
                disabled={!!id}
              >
                <option value="">Select a group</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.code})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Zone Code *</label>
              <input
                type="text"
                value={formData.zoneCode}
                onChange={(e) => {
                  setFormData({ ...formData, zoneCode: e.target.value });
                  setZoneCodeError(undefined);
                  setError(undefined);
                }}
                className={`w-full px-3 py-2 bg-muted border ${
                  zoneCodeError ? 'border-red-500' : 'border-border'
                } rounded-lg text-sm text-foreground focus:outline-none focus:border-ring`}
                required
                disabled={!!id}
              />
              {zoneCodeError && (
                <p className="text-xs text-destructive mt-1">{zoneCodeError}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Zone Name *</label>
            <input
              type="text"
              value={formData.zoneName}
              onChange={(e) => setFormData({ ...formData, zoneName: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Publish Status *</label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as 'NOT_PUBLISHED' | 'PUBLISHED',
                })
              }
              className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
              required
            >
              <option value="NOT_PUBLISHED">Not Published (Draft)</option>
              <option value="PUBLISHED">Published (Active)</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Published zones are visible to users and included in airspace queries
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Active Start</label>
              <input
                type="datetime-local"
                value={formData.activeStart}
                onChange={(e) => setFormData({ ...formData, activeStart: e.target.value })}
                className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Active End</label>
              <input
                type="datetime-local"
                value={formData.activeEnd}
                onChange={(e) => setFormData({ ...formData, activeEnd: e.target.value })}
                className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
              />
            </div>
          </div>
        </div>

        {/* Current Geometry Preview */}
        {id && originalGeometry && (
          <div className="bg-card border border-border rounded-lg p-4 space-y-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <MapPin className="text-primary" size={20} />
                <h2 className="text-xl font-semibold text-foreground">Current Geometry</h2>
              </div>
              <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded">
                {originalGeometry.type}
              </span>
            </div>
            <UtmGeometryPreview geometry={originalGeometry} height="400px" />
            <p className="text-xs text-muted-foreground">
              This is the existing geometry. Upload a new file below to replace it.
            </p>
          </div>
        )}

        {/* Style Settings */}
        <div className="bg-card border border-border rounded-lg p-4 space-y-4 sm:p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Style Settings</h2>
          <p className="text-xs text-muted-foreground">
            Zone style overrides group/category style when provided.
          </p>
          <UtmStyleEditor
            style={style}
            onChange={(nextStyle) => {
              setStyle(nextStyle);
              setStyleTouched(true);
            }}
            previewGeometry={geometry || originalGeometry}
          />
        </div>

        {/* Geometry Upload */}
        <div className="bg-card border border-border rounded-lg p-4 space-y-4 sm:p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            {id && originalGeometry ? 'Update Geometry' : 'Geometry'}
          </h2>
          <UtmGeometryUpload geometry={geometry} onChange={setGeometry} />
        </div>

        {/* Altitude Bounds */}
        {geometry && (
          <div className="bg-card border border-border rounded-lg p-4 space-y-4 sm:p-6">
            <h2 className="text-xl font-semibold text-foreground mb-4">Altitude Bounds</h2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Lower Bound (meters)
                </label>
                <input
                  type="number"
                  value={formData.lowerBoundM}
                  onChange={(e) =>
                    setFormData({ ...formData, lowerBoundM: Number(e.target.value) })
                  }
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Lower Reference</label>
                <select
                  value={formData.lowerRef}
                  onChange={(e) =>
                    setFormData({ ...formData, lowerRef: e.target.value as 'AGL' | 'AMSL' })
                  }
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
                >
                  <option value="AGL">AGL (Above Ground Level)</option>
                  <option value="AMSL">AMSL (Above Mean Sea Level)</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Upper Bound (meters)
                </label>
                <input
                  type="number"
                  value={formData.upperBoundM}
                  onChange={(e) =>
                    setFormData({ ...formData, upperBoundM: Number(e.target.value) })
                  }
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Upper Reference</label>
                <select
                  value={formData.upperRef}
                  onChange={(e) =>
                    setFormData({ ...formData, upperRef: e.target.value as 'AGL' | 'AMSL' })
                  }
                  className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-ring"
                >
                  <option value="AGL">AGL (Above Ground Level)</option>
                  <option value="AMSL">AMSL (Above Mean Sea Level)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end sm:space-x-0">
          <button
            type="button"
            onClick={() => navigate('/admin/uzamaps/zones')}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-muted px-4 py-2 text-foreground transition-colors hover:bg-accent sm:w-auto"
          >
            <X size={18} />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            <Save size={18} />
            <span>{loading ? 'Saving...' : 'Save Zone'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default UtmZoneForm;
